import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { ArrowLeft, Calendar as CalendarIcon, Flag, DollarSign, Play, MapPin, Shield } from "lucide-react";

// Available courses (fetched dynamically)

export default function NewRound() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState(new Date());
  const [courses, setCourses] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    course: "guadalajara_cc",
    point_value: "",
    starting_hole: "1",
    units_advantage_enabled: false,
    units_advantage_divisor: 3,
    invert_advantages: false,
  });

  useEffect(() => {
    fetch(`${API}/courses`).then(r => r.json()).then(setCourses).catch(() => {});
  }, []);

  const selectedCourse = courses.find(c => c.id === formData.course);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error("El nombre de la ronda es requerido");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/rounds`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          name: formData.name,
          date: format(date, "yyyy-MM-dd"),
          course: selectedCourse?.name || "Guadalajara Country Club",
          point_value: formData.point_value ? parseFloat(formData.point_value) : null,
          starting_hole: parseInt(formData.starting_hole),
          units_advantage_enabled: formData.units_advantage_enabled,
          invert_advantages: formData.starting_hole === "10" && formData.invert_advantages,
        }),
      });

      if (response.ok) {
        const round = await response.json();
        toast.success("Ronda creada exitosamente");
        navigate(`/round/${round.round_id}/players`);
      } else {
        throw new Error("Error creating round");
      }
    } catch {
      
      toast.error("Error al crear la ronda");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-lg mx-auto px-4 py-4 flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate("/dashboard")}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-xl font-bold text-[#0F172A]">Nueva Ronda</h1>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Round Name */}
          <Card className="card-default">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <Flag className="w-5 h-5 text-[#1B4D3E]" />
                Información de la Ronda
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="name" className="text-sm font-medium text-[#64748B]">
                  Nombre de la ronda *
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="ej. Domingo con amigos"
                  className="input-default mt-1"
                  data-testid="round-name-input"
                />
              </div>

              <div>
                <Label className="text-sm font-medium text-[#64748B]">Fecha</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal mt-1"
                      data-testid="date-picker-btn"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(date, "PPP", { locale: es })}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={(d) => d && setDate(d)}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div>
                <Label className="text-sm font-medium text-[#64748B]">Campo</Label>
                <div className="mt-2 space-y-2">
                  {courses.map((course) => (
                    <div
                      key={course.id}
                      onClick={() => setFormData({ ...formData, course: course.id })}
                      className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        formData.course === course.id
                          ? "border-[#1B7A4C] bg-[#1B7A4C]/5"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                      data-testid={`course-${course.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <MapPin className={`w-5 h-5 ${formData.course === course.id ? "text-[#1B7A4C]" : "text-gray-400"}`} />
                        <div>
                          <p className={`font-semibold ${formData.course === course.id ? "text-[#1B7A4C]" : "text-[#0F172A]"}`}>
                            {course.name}
                          </p>
                          <p className="text-xs text-[#64748B]">18 hoyos • Par {course.par}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Starting Hole */}
          <Card className="card-default">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <Play className="w-5 h-5 text-[#1B4D3E]" />
                Hoyo de Inicio
              </CardTitle>
            </CardHeader>
            <CardContent>
              <RadioGroup
                value={formData.starting_hole}
                onValueChange={(value) => setFormData({ ...formData, starting_hole: value })}
                className="flex gap-4"
              >
                <div className="flex-1">
                  <Label
                    htmlFor="hole-1"
                    className={`flex flex-col items-center p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      formData.starting_hole === "1"
                        ? "border-[#1B4D3E] bg-[#1B4D3E]/5"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <RadioGroupItem value="1" id="hole-1" className="sr-only" />
                    <span className={`text-3xl font-bold ${formData.starting_hole === "1" ? "text-[#1B4D3E]" : "text-gray-400"}`}>
                      1
                    </span>
                    <span className={`text-sm mt-1 ${formData.starting_hole === "1" ? "text-[#1B4D3E]" : "text-[#64748B]"}`}>
                      Hoyo 1
                    </span>
                    <span className="text-xs text-[#64748B] mt-1">Ida</span>
                  </Label>
                </div>
                <div className="flex-1">
                  <Label
                    htmlFor="hole-10"
                    className={`flex flex-col items-center p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      formData.starting_hole === "10"
                        ? "border-[#1B4D3E] bg-[#1B4D3E]/5"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <RadioGroupItem value="10" id="hole-10" className="sr-only" />
                    <span className={`text-3xl font-bold ${formData.starting_hole === "10" ? "text-[#1B4D3E]" : "text-gray-400"}`}>
                      10
                    </span>
                    <span className={`text-sm mt-1 ${formData.starting_hole === "10" ? "text-[#1B4D3E]" : "text-[#64748B]"}`}>
                      Hoyo 10
                    </span>
                    <span className="text-xs text-[#64748B] mt-1">Vuelta</span>
                  </Label>
                </div>
              </RadioGroup>
              {formData.starting_hole === "10" && (
                <label
                  className={`mt-4 flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${formData.invert_advantages ? "border-[#1B4D3E] bg-[#1B4D3E]/5" : "border-gray-200 hover:border-gray-300"}`}
                  data-testid="invert-advantages-toggle"
                >
                  <input
                    type="checkbox"
                    checked={formData.invert_advantages}
                    onChange={(e) => setFormData({ ...formData, invert_advantages: e.target.checked })}
                    className="w-4 h-4 mt-0.5 rounded border-gray-300 accent-[#1B4D3E]"
                  />
                  <div>
                    <p className="text-sm font-semibold text-[#0F172A]">Invertir ventajas</p>
                    <p className="text-xs text-[#64748B] mt-0.5">La mayoría de las ventajas se aplican en la vuelta del 10 (que se juega primero). Ej. con 7 ventajas: 4 en la vuelta del 10 y 3 en la del 1.</p>
                  </div>
                </label>
              )}
            </CardContent>
          </Card>

          {/* Point Value (Optional) */}
          <Card className="card-default">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-[#D4A373]" />
                Valor por Punto (Opcional)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div>
                <Label htmlFor="point_value" className="text-sm font-medium text-[#64748B]">
                  Cada punto ganado vale
                </Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]">$</span>
                  <Input
                    id="point_value"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.point_value}
                    onChange={(e) => setFormData({ ...formData, point_value: e.target.value })}
                    placeholder="0.00"
                    className="input-default pl-8"
                    data-testid="point-value-input"
                  />
                </div>
                <p className="text-xs text-[#64748B] mt-2">
                  Las unidades (birdie, sandy, oyes) también cuentan como puntos
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Unidades de Ventaja */}
          <Card className="card-default">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Shield className="w-5 h-5 text-[#D4A373]" />
                  <div>
                    <p className="font-semibold text-[#1B4D3E]">Unidades de Ventaja</p>
                    <p className="text-xs text-[#64748B] mt-0.5">
                      Por cada {formData.units_advantage_divisor} golpes de ventaja, 1 unidad compensatoria
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={formData.units_advantage_enabled}
                  data-testid="units-advantage-toggle"
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    formData.units_advantage_enabled ? 'bg-[#1B4D3E]' : 'bg-gray-300'
                  }`}
                  onClick={() => setFormData({ ...formData, units_advantage_enabled: !formData.units_advantage_enabled })}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    formData.units_advantage_enabled ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>
              {formData.units_advantage_enabled && (
                <div className="mt-3 space-y-3">
                  <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                    <span className="text-sm text-[#0F172A] font-medium">Golpes por unidad</span>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        data-testid="ua-divisor-minus"
                        className="w-8 h-8 rounded-full bg-[#1B4D3E] text-white flex items-center justify-center text-lg font-bold disabled:opacity-30"
                        disabled={formData.units_advantage_divisor <= 2}
                        onClick={() => setFormData({ ...formData, units_advantage_divisor: formData.units_advantage_divisor - 1 })}
                      >-</button>
                      <span className="text-2xl font-bold text-[#1B4D3E] w-8 text-center" data-testid="ua-divisor-value">
                        {formData.units_advantage_divisor}
                      </span>
                      <button
                        type="button"
                        data-testid="ua-divisor-plus"
                        className="w-8 h-8 rounded-full bg-[#1B4D3E] text-white flex items-center justify-center text-lg font-bold disabled:opacity-30"
                        disabled={formData.units_advantage_divisor >= 10}
                        onClick={() => setFormData({ ...formData, units_advantage_divisor: formData.units_advantage_divisor + 1 })}
                      >+</button>
                    </div>
                  </div>
                  <p className="text-xs text-[#92400e] bg-[#D4A373]/10 rounded-lg p-3 border border-[#D4A373]/20">
                    Las unidades de ventaja solo cancelan unidades perdidas. 
                    Nunca generan puntos positivos. Los oyes NO se ven afectados.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          <Button 
            type="submit" 
            className="btn-primary w-full py-6"
            disabled={loading}
            data-testid="create-round-btn"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Creando...
              </span>
            ) : (
              "Continuar a Jugadores"
            )}
          </Button>
        </form>
      </main>
    </div>
  );
}
